/**
 * File: projectStateService.ts
 * Description: Comprehensive project state management service for all pages
 * Author: System
 * Created: 2025-08-11
 * Last Modified: 2025-10-31
 */

import { TOTAL_ARCHITECTURE_SECTIONS } from '../types/generation';
import { ProjectItem, ProjectStatus, ProjectType } from '../types/project';
import { logger } from '../utils/logger';

import { projectService } from './projectService';

const DEFAULT_PROJECT_PROGRESS: ProjectItem['progress'] = {
  contextUploaded: false,
  contextReviewed: false,
  architectureGenerated: false,
  sectionsCompleted: 0,
  totalSections: TOTAL_ARCHITECTURE_SECTIONS,
};

/**
 * Canonical forward order of project lifecycle statuses. Used to guarantee that
 * status only ever advances and never moves backwards when a later stage is
 * reached (e.g. generating ADR suggestions advances the project to GENERATION).
 */
const STATUS_ADVANCE_ORDER: ProjectStatus[] = [
  ProjectStatus.CONTEXT_UPLOAD,
  ProjectStatus.CONTEXT_REVIEW,
  ProjectStatus.ADR,
  ProjectStatus.GENERATION,
  ProjectStatus.BIZZDESIGN_INTEGRATION,
  ProjectStatus.IN_REVIEW,
  ProjectStatus.COMPLETED,
  ProjectStatus.PUBLISHED,
];

const DEFAULT_PROJECT_REVIEW_SUMMARY: ProjectItem['review_summary'] = {
  review_id: null,
  status: 'not_submitted',
  reviewer_count: 0,
  approver_count: 0,
  approver_missing: false,
  approver_names: [],
  approved_at: null,
  updated_at: null,
};

const DEFAULT_PROJECT_PUBLISH_SUMMARY: ProjectItem['publish_summary'] = {
  is_published: false,
  can_publish: false,
  published_by: null,
  published_at: null,
};

function createDefaultProjectItem(projectId: number): ProjectItem {
  const now = new Date().toISOString();

  return {
    id: projectId,
    title: '',
    description: '',
    project_type: ProjectType.GREEN_FIELD,
    line_of_business: null,
    status: ProjectStatus.CONTEXT_UPLOAD,
    original_link: null,
    owner: '',
    owner_display_name: null,
    co_owners: null,
    created_at: now,
    updated_at: now,
    review_summary: { ...DEFAULT_PROJECT_REVIEW_SUMMARY },
    publish_summary: { ...DEFAULT_PROJECT_PUBLISH_SUMMARY },
    projectType: ProjectType.GREEN_FIELD,
    projectLineOfBusiness: null,
    originalProjectLink: '',
    progress: { ...DEFAULT_PROJECT_PROGRESS },
  };
}

// ======================
// State Interfaces
// ======================

export interface ProjectSetupState {
  title: string;
  description: string;
  projectType: 'green_field' | 'brown_field' | '';
  projectLineOfBusiness: string;
  originalProjectLink?: string;
  lastModified: string;
}

const getProjectSetupLineOfBusiness = (
  setupState: Partial<ProjectSetupState>,
  project: ProjectItem,
): string | null => {
  if (setupState.projectLineOfBusiness !== undefined) {
    return setupState.projectLineOfBusiness || null;
  }

  return project.projectLineOfBusiness ?? project.line_of_business ?? null;
};

export interface ContextManagementState {
  uploadedDocuments: UploadedDocument[];
  selectedDocumentIds: string[];
  processingStatus: Record<string, 'pending' | 'processing' | 'completed' | 'error'>;
  contextSummary?: string;
  projectInstructions?: string;
  originalProjectLink?: string;
  selectedDocumentType?: string;
  // Store metadata for knowledge base documents (search results) that were added to context
  knowledgeBaseDocuments?: Record<string, {
    title: string;
    summary?: string;
    description?: string;
    type?: string;
    category?: string;
    tags?: string[];
    author?: string;
    lastModified?: string;
    source?: 'wiki' | 'guidelines' | 'user_docs';
    wiki_path?: string;
  }>;
  lastModified: string;
}

export interface UploadedDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadDate: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  summary?: string;
  metadata?: Record<string, unknown>;
}

export interface CRAState {
  reviewStatus: 'not_started' | 'in_progress' | 'completed';
  approvedSections: string[];
  comments: CRAComment[];
  contextAnalysis?: string;
  readinessScore?: number;
  lastModified: string;
}

export interface CRAComment {
  id: string;
  section: string;
  comment: string;
  author: string;
  timestamp: string;
  resolved: boolean;
}

export interface ADRState {
  decisions: ADRDecision[];
  templates: ADRTemplate[];
  currentDecisionId?: string;
  lastModified: string;
  lastActionTimestamp?: string;
}

export interface ADRDecision {
  id: string;
  title: string;
  status: 'proposed' | 'accepted' | 'rejected' | 'superseded';
  content: string;
  context: string;
  decision: string;
  consequences: string;
  alternatives?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ADRTemplate {
  id: string;
  name: string;
  description: string;
  template: string;
}

export interface GenerationState {
  selectedSections: string[];
  generationMode: 'complete' | 'selective' | 'single';
  currentSection?: string;
  section_id?: string; // Track which section is currently in focus
  progress: GenerationProgress;
  generatedSections: GeneratedSection[];
  settings: GenerationSettings;
  lastModified: string;
}

export interface GenerationProgress {
  totalSections: number;
  completedSections: number;
  currentSectionIndex: number;
  overallPercentage: number;
  startTime?: string;
  endTime?: string;
}

export interface GeneratedSection {
  id: string;
  title: string;
  content: string;
  status: 'pending' | 'generating' | 'completed' | 'error';
  quality?: QualityMetrics;
  createdAt: string;
  updatedAt: string;
}

export interface QualityMetrics {
  groundednessScore: number;  
  completenessScore: number;
  consistencyScore: number;
  overallScore: number;
}

export interface GenerationSettings {
  includeReferences: boolean;
  detailLevel: 'high' | 'medium' | 'low';
  outputFormat: 'markdown' | 'html';
  customPrompts: Record<string, string>;
}

export interface ReviewState {
  approvalStatus: 'pending' | 'approved' | 'needs_changes';
  reviewComments: ReviewComment[];
  finalDocument?: string;
  exportSettings: ExportSettings;
  sharing: SharingSettings;
  lastModified: string;
}

export interface ReviewComment {
  id: string;
  section: string;
  comment: string;
  type: 'suggestion' | 'required_change' | 'approval';
  author: string;
  timestamp: string;
  resolved: boolean;
}

export interface ExportSettings {
  format: 'pdf' | 'docx' | 'html' | 'markdown';
  includeDiagrams: boolean;
  includeReferences: boolean;
  includeAppendices: boolean;
}

export interface SharingSettings {
  shareableLink?: string;
  emailRecipients: string[];
  accessLevel: 'view' | 'comment' | 'edit';
}

export interface ProjectState {
  project: ProjectItem;
  setup: ProjectSetupState;
  context: ContextManagementState;
  cra: CRAState;
  adrs: ADRState;
  generation: GenerationState;
  review: ReviewState;
  lastSavedAt: string;
  version: number;
  /** The last page route visited, used to restore position when re-opening a project. */
  lastVisitedRoute?: string;
}

// ======================
// Service Implementation
// ======================

class ProjectStateService {
  private readonly STORAGE_PREFIX = 'archie_project_state';
  private readonly VERSION = 2; // Incremented for new key structure
  private currentUserId: string | null = null;

  /**
   * Set the current user ID for localStorage key generation
   * Should be called when user logs in or app initializes
   */
  setUserId(userId: string): void {
    this.currentUserId = userId;
  }

  /**
   * Get the storage key for a project, including user_id
   * Format: archie_project_state_user_{userId}_project_{projectId}
   */
  private getStorageKey(projectId: number): string {
    if (!this.currentUserId) {
      // Fallback to user from localStorage if not set
      const storedUser = localStorage.getItem('auth_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          this.currentUserId = userData.id?.toString() || userData.email || 'anonymous';
        } catch {
          this.currentUserId = 'anonymous';
        }
      } else {
        this.currentUserId = 'anonymous';
      }
    }
    
    return `${this.STORAGE_PREFIX}_user_${this.currentUserId}_project_${projectId}`;
  }

  /**
   * Get legacy storage key (for migration)
   */
  private getLegacyStorageKey(projectId: number): string {
    return `archie_project_state_${projectId}`;
  }

  /**
   * Migrate from old storage key to new key structure
   */
  private migrateStorage(projectId: number): ProjectState | null {
    const legacyKey = this.getLegacyStorageKey(projectId);
    const legacyData = localStorage.getItem(legacyKey);
    
    if (!legacyData) {
      return null;
    }

    try {
      const state = JSON.parse(legacyData) as ProjectState;
      
      // Save to new key structure
      const newKey = this.getStorageKey(projectId);
      localStorage.setItem(newKey, legacyData);
      
      // Remove legacy key
      localStorage.removeItem(legacyKey);
      
      
      return state;
    } catch (error) {
      logger.error('Error migrating storage:', error);
      return null;
    }
  }

  /**
   * Initialize default state for a new project
   */
  private getDefaultState(project: ProjectItem): ProjectState {
    const now = new Date().toISOString();
    
    return {
      project,
      setup: {
        title: project.title,
        description: project.description || '',
        projectType: project.projectType || '',
        projectLineOfBusiness:
          project.projectLineOfBusiness ?? project.line_of_business ?? '',
        originalProjectLink: project.originalProjectLink,
        lastModified: now,
      },
      context: {
        uploadedDocuments: [],
        selectedDocumentIds: [],
        processingStatus: {},
        lastModified: now,
      },
      cra: {
        reviewStatus: 'not_started',
        approvedSections: [],
        comments: [],
        lastModified: now,
      },
      adrs: {
        decisions: [],
        templates: this.getDefaultADRTemplates(),
        lastModified: now,
      },
      generation: {
        selectedSections: [],
        generationMode: 'complete',
        section_id: undefined,
        progress: {
          totalSections: 0,
          completedSections: 0,
          currentSectionIndex: 0,
          overallPercentage: 0,
        },
        generatedSections: [],
        settings: {
          includeReferences: true,
          detailLevel: 'high',
          outputFormat: 'markdown',
          customPrompts: {},
        },
        lastModified: now,
      },
      review: {
        approvalStatus: 'pending',
        reviewComments: [],
        exportSettings: {
          format: 'pdf',
          includeDiagrams: true,
          includeReferences: true,
          includeAppendices: true,
        },
        sharing: {
          emailRecipients: [],
          accessLevel: 'view',
        },
        lastModified: now,
      },
      lastSavedAt: now,
      version: this.VERSION,
    };
  }

  /**
   * Get default ADR templates
   */
  private getDefaultADRTemplates(): ADRTemplate[] {
    return [
      {
        id: 'basic',
        name: 'Basic ADR Template',
        description: 'Standard architecture decision record template',
        template: `# ADR-{number}: {title}

## Status
{status}

## Context
{context}

## Decision
{decision}

## Consequences
{consequences}

## Alternatives Considered
{alternatives}`,
      },
      {
        id: 'detailed',
        name: 'Detailed ADR Template',
        description: 'Comprehensive template with additional sections',
        template: `# ADR-{number}: {title}

## Status
{status}

## Context
{context}

## Decision
{decision}

## Rationale
{rationale}

## Consequences
### Positive
{positive_consequences}

### Negative
{negative_consequences}

## Alternatives Considered
{alternatives}

## Implementation Notes
{implementation}

## Related Decisions
{related}`,
      },
    ];
  }

  /**
   * Get existing project state from storage without creating defaults
   * @private
   */
  private async getExistingProjectState(projectId: number): Promise<ProjectState | null> {
    try {
      const storageKey = this.getStorageKey(projectId);
      const saved = localStorage.getItem(storageKey);
      
      // Try migration if not found
      if (!saved) {
        const migratedState = this.migrateStorage(projectId);
        if (migratedState) {
          return migratedState;
        }
        return null;
      }

      const state = JSON.parse(saved) as ProjectState;
      
      // Validate version and structure
      if (!state.version || state.version < this.VERSION) {
        logger.warn(
          `Project ${projectId} state version outdated ` +
          `(v${state.version || 0}), migrating to v${this.VERSION}`,
        );
        
        // Migrate state structure
        const migratedState = this.migrateStateVersion(state);
        
        // Save migrated state
        localStorage.setItem(storageKey, JSON.stringify(migratedState));
        
        return migratedState;
      }

      return state;
    } catch (error) {
      logger.error('Error reading existing project state:', error);
      return null;
    }
  }

  /**
   * Migrate state from older versions to current version
   */
  private migrateStateVersion(oldState: ProjectState): ProjectState {
    const now = new Date().toISOString();
    
    // Version 1 to Version 2 migration
    if (!oldState.version || oldState.version < 2) {
      // Add section_id to generation state if missing
      if (oldState.generation && !('section_id' in oldState.generation)) {
        oldState.generation.section_id = undefined;
      }
    }
    
    return {
      ...oldState,
      version: this.VERSION,
      lastSavedAt: now,
    };
  }

  /**
   * Save complete project state
   */
  async saveProjectState(projectId: number, state: Partial<ProjectState>): Promise<void> {
    try {
      const existingState = await this.getExistingProjectState(projectId);
      
      let updatedState: ProjectState;
      
      if (existingState) {
        // Merge with existing state
        updatedState = {
          ...existingState,
          ...state,
          lastSavedAt: new Date().toISOString(),
          version: this.VERSION,
        };
      } else {
        // Create new state from provided partial state
        if (!state.project) {
          throw new Error('Cannot save project state without project data');
        }
        
        const defaultState = this.getDefaultState(state.project);
        updatedState = {
          ...defaultState,
          ...state,
          lastSavedAt: new Date().toISOString(),
          version: this.VERSION,
        };
      }

      const storageKey = this.getStorageKey(projectId);
      localStorage.setItem(storageKey, JSON.stringify(updatedState));
      
    } catch (error) {
      logger.error('Error saving project state:', error);
      throw new Error('Failed to save project state');
    }
  }

  /**
   * Get complete project state
   */
  async getProjectState(projectId: number): Promise<ProjectState> {
    try {
      // First try to get existing state
      const existingState = await this.getExistingProjectState(projectId);
      
      if (existingState) {
        return existingState;
      }

      // No existing state - create default but don't save it yet
      // This avoids the circular dependency
      logger.warn(`Project ${projectId} not found in state storage, creating default state`);
      
      const defaultProject = createDefaultProjectItem(projectId);

      const defaultState = this.getDefaultState(defaultProject);
      
      // Save the default state directly to localStorage to avoid circular call
      const storageKey = this.getStorageKey(projectId);
      localStorage.setItem(storageKey, JSON.stringify(defaultState));
      
      return defaultState;
    } catch (error) {
      logger.error('Error loading project state:', error);
      
      // If it's a parsing error or storage issue, create default state
      const errorWithName = error as { name?: string };
      if (error instanceof SyntaxError || errorWithName?.name === 'QuotaExceededError') {
        logger.warn('Storage issue detected, creating default state');
        const defaultProject = createDefaultProjectItem(projectId);
        return this.getDefaultState(defaultProject);
      }
      
      throw error;
    }
  }

  /**
   * Initialize state for a new project
   */
  async initializeProjectState(project: ProjectItem): Promise<ProjectState> {
    const defaultState = this.getDefaultState(project);
    await this.saveProjectState(project.id, defaultState);
    return defaultState;
  }

  /**
   * Save specific page state
   */
  async savePageState(
    projectId: number, 
    page: keyof Omit<ProjectState, 'project' | 'lastSavedAt' | 'version'>, 
    pageState: unknown,
  ): Promise<void> {
    const updatedPageState = {
      ...(pageState as Record<string, unknown>),
      lastModified: new Date().toISOString(),
    };

    await this.saveProjectState(projectId, {
      [page]: updatedPageState,
    });
  }

  /**
   * Get specific page state
   */
  async getPageState<T>(
    projectId: number, 
    page: keyof Omit<ProjectState, 'project' | 'lastSavedAt' | 'version'>,
  ): Promise<T> {
    const state = await this.getProjectState(projectId);
    return state[page] as T;
  }

  /**
   * Update project status in both localStorage and the backend.
   */
  async updateProjectStatus(projectId: number, status: ProjectStatus): Promise<void> {
    const currentState = await this.getProjectState(projectId);
    const updatedProject = {
      ...currentState.project,
      status,
      updated_at: new Date().toISOString(),
    };

    await this.saveProjectState(projectId, {
      project: updatedProject,
    });

    try {
      await projectService.updateProject(projectId, { status });
    } catch (error) {
      logger.error('Failed to sync project status to backend:', error);
    }
  }

  /**
   * Advance the project status to the target only when it lies further along
   * the lifecycle than the current status. Moving backwards is a no-op, so
   * revisiting an earlier page never regresses progress.
   */
  async advanceProjectStatus(
    projectId: number,
    targetStatus: ProjectStatus,
  ): Promise<void> {
    try {
      const currentState = await this.getProjectState(projectId);
      const currentStatus = currentState.project.status ?? ProjectStatus.CONTEXT_UPLOAD;
      const currentIndex = STATUS_ADVANCE_ORDER.indexOf(currentStatus as ProjectStatus);
      const targetIndex = STATUS_ADVANCE_ORDER.indexOf(targetStatus);

      if (targetIndex > currentIndex) {
        await this.updateProjectStatus(projectId, targetStatus);
      }
    } catch (error) {
      logger.error('Failed to advance project status:', error);
    }
  }

  /**
   * Persist the last page the user was on for a project.
   * Call this before navigating away so re-opening the project
   * returns to the correct page.
   */
  async saveLastVisitedRoute(
    projectId: number,
    route: string,
  ): Promise<void> {
    await this.saveProjectState(projectId, { lastVisitedRoute: route });
  }

  /**
   * Get the route to navigate to when re-opening a project.
   * Returns the last visited route when stored, otherwise falls back
   * to the default route derived from project status.
   */
  async getLastPageRoute(
    projectId: number,
    status: ProjectStatus,
  ): Promise<string> {
    const statusRoutes: Record<ProjectStatus, string> = {
      [ProjectStatus.CONTEXT_UPLOAD]: `/project/${projectId}/context`,
      [ProjectStatus.CONTEXT_REVIEW]: `/project/${projectId}/cra`,
      [ProjectStatus.ADR]: `/project/${projectId}/adrs`,
      [ProjectStatus.GENERATION]: `/project/${projectId}/generation`,
      [ProjectStatus.BIZZDESIGN_INTEGRATION]: `/project/${projectId}/c4`,
      [ProjectStatus.IN_REVIEW]: `/project/${projectId}/review`,
      [ProjectStatus.COMPLETED]: `/project/${projectId}/review`,
      [ProjectStatus.PUBLISHED]: `/project/${projectId}/review`,
    };

    try {
      const state = await this.getExistingProjectState(projectId);
      if (state?.lastVisitedRoute) {
        return state.lastVisitedRoute;
      }
    } catch {
      // Fall back to status-based route
    }

    return statusRoutes[status] ?? `/project/${projectId}/setup`;
  }

  /**
   * Clear project state (useful for cleanup)
   */
  async clearProjectState(projectId: number): Promise<void> {
    const storageKey = this.getStorageKey(projectId);
    localStorage.removeItem(storageKey);
    
    // Also remove legacy key if it exists
    const legacyKey = this.getLegacyStorageKey(projectId);
    localStorage.removeItem(legacyKey);
    
  }

  /**
   * Get all saved project states (for debugging/management)
   */
  getAllSavedStates(): { projectId: string; userId: string; lastSaved: string }[] {
    const states: { projectId: string; userId: string; lastSaved: string }[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        // Parse key: archie_project_state_user_{userId}_project_{projectId}
        const match = key.match(/archie_project_state_user_(.+)_project_(\d+)/);
        
        if (match) {
          const userId = match[1];
          const projectId = match[2];
          const stateData = localStorage.getItem(key);
          
          if (stateData) {
            try {
              const state: ProjectState = JSON.parse(stateData);
              states.push({
                projectId,
                userId,
                lastSaved: state.lastSavedAt,
              });
            } catch {
              logger.warn(`Invalid state data for project ${projectId}`);
            }
          }
        }
      }
    }
    
    return states;
  }

  /**
   * Auto-save functionality with debouncing
   */
  private autoSaveTimeouts: Map<string, NodeJS.Timeout> = new Map();

  debouncedSave(
    projectId: number, 
    page: keyof Omit<ProjectState, 'project' | 'lastSavedAt' | 'version'>, 
    pageState: unknown, 
    delay: number = 2000,
  ): void {
    const key = `${projectId}_${page}`;
    
    // Clear existing timeout
    const existingTimeout = this.autoSaveTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      try {
        await this.savePageState(projectId, page, pageState);
      } catch (error) {
        logger.error(`Auto-save failed for ${page}:`, error);
      }
      this.autoSaveTimeouts.delete(key);
    }, delay);

    this.autoSaveTimeouts.set(key, timeout);
  }

  /**
   * Update the currently focused section in generation state
   * @param projectId - The project ID
   * @param sectionId - The section ID that is currently focused
   */
  async updateFocusedSection(projectId: number, sectionId: string | null): Promise<void> {
    try {
      const currentState = await this.getProjectState(projectId);
      
      await this.saveProjectState(projectId, {
        generation: {
          ...currentState.generation,
          section_id: sectionId || undefined,
          lastModified: new Date().toISOString(),
        },
      });
    } catch (error) {
      logger.error('Failed to update focused section:', error);
    }
  }

  /**
   * Update the ADR action timestamp - records the most recent ADR-related activity
   * @param projectId - The project ID
   * @param actionTimestamp - ISO timestamp of the ADR action (create/update/delete)
   */
  async updateAdrActionTimestamp(projectId: number, actionTimestamp: string): Promise<void> {
    try {
      const currentState = await this.getProjectState(projectId);
      
      await this.saveProjectState(projectId, {
        adrs: {
          ...currentState.adrs,
          lastActionTimestamp: actionTimestamp,
          lastModified: new Date().toISOString(),
        },
      });
    } catch (error) {
      logger.error('Failed to update ADR action timestamp:', error);
    }
  }

  /**
   * Get the currently focused section
   * @param projectId - The project ID
   * @returns The section ID that is currently focused, or null
   */
  async getFocusedSection(projectId: number): Promise<string | null> {
    try {
      const state = await this.getProjectState(projectId);
      return state.generation.section_id || null;
    } catch (error) {
      logger.error('Failed to get focused section:', error);
      return null;
    }
  }

  // ======================
  // Cleanup & Maintenance
  // ======================

  /**
   * Configuration for cleanup policies
   */
  private readonly CLEANUP_CONFIG = {
    MAX_AGE_DAYS: 30,           // Delete states older than 30 days
    MAX_STORAGE_MB: 5,          // Alert if storage exceeds 5MB
    CLEANUP_INTERVAL_MS: 3600000, // Run cleanup every hour
  };

  private cleanupInterval?: NodeJS.Timeout;

  /**
   * Clean up old/unused project states based on age and backend validation
   * @param maxAgeDays - Maximum age in days before state is considered stale
   * @returns Object with cleanup statistics
   */
  async cleanupOldStates(
    maxAgeDays: number = this.CLEANUP_CONFIG.MAX_AGE_DAYS,
  ): Promise<{
      removed: number;
      kept: number;
      errors: string[];
    }> {
    const stats = { removed: 0, kept: 0, errors: [] as string[] };
    const now = new Date();
    const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;

    try {
      // Get all active projects from backend
      const activeProjects = await projectService.getProjects();
      const activeProjectIds = new Set(activeProjects.map(p => p.id.toString()));

      // Get all saved states
      const savedStates = this.getAllSavedStates();

      for (const { projectId, lastSaved } of savedStates) {
        try {
          const lastSavedDate = new Date(lastSaved);
          const age = now.getTime() - lastSavedDate.getTime();

          // Remove if project no longer exists in backend
          if (!activeProjectIds.has(projectId)) {
            await this.clearProjectState(Number.parseInt(projectId, 10));
            stats.removed++;
            continue;
          }

          // Remove if state is too old
          if (age > maxAgeMs) {
            await this.clearProjectState(Number.parseInt(projectId, 10));
            stats.removed++;
            continue;
          }

          stats.kept++;
        } catch (error) {
          const errorMessage = 
            `Failed to process project ${projectId}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          stats.errors.push(errorMessage);
        }
      }
    } catch (error) {
      stats.errors.push(
        `Cleanup failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }

    return stats;
  }

  /**
   * Get localStorage usage statistics
   * @returns Storage usage information
   */
  getStorageStats(): {
    usedBytes: number;
    usedMB: number;
    projectCount: number;
    oldestState: string | null;
    newestState: string | null;
    statesByUser: Record<string, number>;
    } {
    const states = this.getAllSavedStates();
    let totalBytes = 0;
    const statesByUser: Record<string, number> = {};

    // Calculate total storage used by project states
    for (const { projectId, userId } of states) {
      const storageKey = `${this.STORAGE_PREFIX}_user_${userId}_project_${projectId}`;
      const data = localStorage.getItem(storageKey);
      if (data) {
        totalBytes += new Blob([data]).size;
      }
      
      // Count states by user
      statesByUser[userId] = (statesByUser[userId] || 0) + 1;
    }

    // Find oldest and newest states
    const sortedByDate = states.sort(
      (a, b) => new Date(a.lastSaved).getTime() - new Date(b.lastSaved).getTime(),
    );

    return {
      usedBytes: totalBytes,
      usedMB: Math.round((totalBytes / (1024 * 1024)) * 100) / 100,
      projectCount: states.length,
      oldestState: sortedByDate[0]?.lastSaved || null,
      newestState: sortedByDate[sortedByDate.length - 1]?.lastSaved || null,
      statesByUser,
    };
  }

  /**
   * Start automatic cleanup process
   * @param intervalMs - Interval in milliseconds between cleanup runs
   */
  startAutoCleanup(intervalMs: number = this.CLEANUP_CONFIG.CLEANUP_INTERVAL_MS): void {
    // Clear existing interval if any
    this.stopAutoCleanup();

    // Run cleanup immediately
    void this.cleanupOldStates();

    // Schedule periodic cleanup
    this.cleanupInterval = setInterval(() => {
      void this.cleanupOldStates();
    }, intervalMs);
  }

  /**
   * Stop automatic cleanup process
   */
  stopAutoCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
  }

  // ======================
  // Backend Synchronization
  // ======================

  /**
   * Sync critical state changes with backend
  * Critical changes include: project status, title, description, type,
  * and project line of business
   */
  async syncWithBackend(projectId: number, updates: Partial<ProjectState>): Promise<void> {
    try {
      // Sync project metadata if changed
      if (updates.project || updates.setup) {
        const currentState = await this.getProjectState(projectId);
        const setupState = updates.setup || currentState.setup;
        const projectData = updates.project || currentState.project;

        // Update project in backend
        await projectService.updateProject(projectId, {
          title: setupState.title || projectData.title,
          description: setupState.description || projectData.description,
          lineOfBusiness: getProjectSetupLineOfBusiness(setupState, projectData),
          status: projectData.status,
        });
      }

      // Sync project status change
      if (updates.project?.status) {
        await projectService.updateProject(projectId, {
          status: updates.project.status,
        });
      }

      // Save to localStorage after successful backend sync
      await this.saveProjectState(projectId, updates);
    } catch (error) {
      logger.error('Failed to sync with backend:', error);
      // Save to localStorage even if backend sync fails
      await this.saveProjectState(projectId, updates);
      throw error;
    }
  }

  /**
   * Update project status with backend sync
   */
  async updateProjectStatusWithSync(
    projectId: number, 
    status: ProjectStatus,
  ): Promise<void> {
    const currentState = await this.getProjectState(projectId);
    const updatedProject = {
      ...currentState.project,
      status,
      updated_at: new Date().toISOString(),
    };

    // Sync with backend first, then update local state
    await this.syncWithBackend(projectId, {
      project: updatedProject,
    });
  }

  /**
   * Save page state with optional backend sync for critical data
   * @param projectId - The project ID
   * @param page - The page state to save
   * @param pageState - The state data
   * @param syncToBackend - Whether to sync critical data to backend
   */
  async savePageStateWithSync(
    projectId: number, 
    page: keyof Omit<ProjectState, 'project' | 'lastSavedAt' | 'version'>, 
    pageState: unknown,
    syncToBackend: boolean = false,
  ): Promise<void> {
    const updatedPageState = {
      ...(pageState as Record<string, unknown>),
      lastModified: new Date().toISOString(),
    };

    if (syncToBackend && page === 'setup') {
      // Sync setup changes with backend
      await this.syncWithBackend(projectId, {
        setup: updatedPageState as ProjectSetupState,
      });
    } else {
      // Just save locally
      await this.saveProjectState(projectId, {
        [page]: updatedPageState,
      });
    }
  }

  /**
   * Batch sync multiple projects with backend
   * Useful for ensuring consistency after reconnection
   */
  async batchSyncWithBackend(projectIds: number[]): Promise<{
    synced: number;
    failed: number;
    errors: string[];
  }> {
    const results = { synced: 0, failed: 0, errors: [] as string[] };

    for (const projectId of projectIds) {
      try {
        const state = await this.getProjectState(projectId);
        
        // Update backend with latest local state
        await projectService.updateProject(projectId, {
          title: state.setup.title,
          description: state.setup.description,
          lineOfBusiness: getProjectSetupLineOfBusiness(
            state.setup,
            state.project,
          ),
          status: state.project.status,
        });
        
        results.synced++;
      } catch (error) {
        results.failed++;
        results.errors.push(
          `Project ${projectId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      }
    }

    return results;
  }
}

// Export singleton instance
export const projectStateService = new ProjectStateService();
